package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.components.SearchBar
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminFixtureResponse
import twist.mena.sports.shared.dto.admin.OffsetPage

@Composable
fun FixturesScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminFixtureResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var offset by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(search, offset) {
        state = UiState.Loading
        scope.launch {
            try {
                val page = apiClient.getFixtures(search.ifBlank { null }, offset)
                state = UiState.Success(page)
            } catch (e: Exception) {
                state = UiState.Error("Failed to load fixtures")
            }
        }
    }

    SearchBar(placeholderText = "Search fixtures...") { query ->
        search = query
        offset = 0
    }

    when (val s = state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val page = s.data
            val columns = listOf(
                Column<AdminFixtureResponse>("Match") { fixture ->
                    Text("${fixture.homeTeamName ?: "?"} vs ${fixture.awayTeamName ?: "?"}")
                },
                Column("League") { fixture ->
                    Text(fixture.leagueName ?: "\u2014")
                },
                Column("Score") { fixture ->
                    Text("${fixture.homeScore ?: "-"} - ${fixture.awayScore ?: "-"}")
                },
                Column("Status") { fixture ->
                    Text(fixture.status)
                },
                Column("Kickoff (UTC)") { fixture ->
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                        Text(fixture.kickoffAtUtc?.take(16) ?: "\u2014")
                    }
                },
                Column("Season") { fixture ->
                    Text(fixture.seasonYear.toString())
                }
            )

            DataTable(
                columns = columns,
                rows = page.data,
                total = page.total,
                offset = page.offset,
                limit = page.limit,
                onPageChange = { newOffset -> offset = newOffset }
            )
        }
    }
}
